import { useEffect, useRef, useState, useCallback } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { getAccessToken } from 'core/auth/accessTokenStorage';

export interface SignalRConfig {
  url: string;
  automaticReconnect?: boolean;
  reconnectDelays?: number[];
}

export interface ConnectionState {
  connection: HubConnection | null;
  connectionState: HubConnectionState;
  isConnected: boolean;
  error: string | null;
}

export const useSignalR = (config: SignalRConfig) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    connection: null,
    connectionState: HubConnectionState.Disconnected,
    isConnected: false,
    error: null,
  });

  const connectionRef = useRef<HubConnection | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const createConnection = useCallback(() => {
    const builder = new HubConnectionBuilder()
      .withUrl(config.url, {
        accessTokenFactory: () => getAccessToken() || '',
      });

    if (config.automaticReconnect) {
      builder.withAutomaticReconnect(config.reconnectDelays || [0, 2000, 10000, 30000]);
    }

    return builder.build();
  }, [config.url, config.automaticReconnect, config.reconnectDelays]);

  const connect = useCallback(async () => {
    try {
      if (connectionRef.current?.state === HubConnectionState.Connected) {
        return;
      }

      if (connectionRef.current) {
        await connectionRef.current.stop();
      }

      const newConnection = createConnection();
      connectionRef.current = newConnection;

      // Event handlers
      newConnection.onclose((error) => {
        setConnectionState(prev => ({
          ...prev,
          connection: newConnection,
          connectionState: HubConnectionState.Disconnected,
          isConnected: false,
          error: error?.message || null,
        }));
      });

      newConnection.onreconnecting((error) => {
        setConnectionState(prev => ({
          ...prev,
          connection: newConnection,
          connectionState: HubConnectionState.Reconnecting,
          isConnected: false,
          error: error?.message || null,
        }));
      });

      newConnection.onreconnected(() => {
        setConnectionState(prev => ({
          ...prev,
          connection: newConnection,
          connectionState: HubConnectionState.Connected,
          isConnected: true,
          error: null,
        }));
      });

      // Start connection
      await newConnection.start();

      setConnectionState({
        connection: newConnection,
        connectionState: HubConnectionState.Connected,
        isConnected: true,
        error: null,
      });

    } catch (error) {
      setConnectionState(prev => ({
        ...prev,
        connection: connectionRef.current,
        connectionState: HubConnectionState.Disconnected,
        isConnected: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      }));
    }
  }, [createConnection]);

  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch {
        // Connection already closing or closed
      }
      connectionRef.current = null;
    }
  }, []);

  const invoke = useCallback(async (methodName: string, ...args: any[]) => {
    if (!connectionRef.current || connectionRef.current.state !== HubConnectionState.Connected) {
      console.warn('Cannot invoke method: Connection is not established');
      return; // Return undefined instead of throwing
    }
    return await connectionRef.current.invoke(methodName, ...args);
  }, []);

  const on = useCallback((methodName: string, handler: (...args: any[]) => void) => {
    if (connectionRef.current) {
      connectionRef.current.on(methodName, handler);
    }
  }, []);

  const off = useCallback((methodName: string, handler?: (...args: any[]) => void) => {
    if (connectionRef.current) {
      if (handler) {
        connectionRef.current.off(methodName, handler);
      } else {
        connectionRef.current.off(methodName);
      }
    }
  }, []);

  // Capture sécurisée de la référence pour le cleanup
  useEffect(() => {
    return () => {
      const currentTimeout = reconnectTimeoutRef.current;
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      disconnect();
    };
  }, [disconnect]);

  return {
    ...connectionState,
    connect,
    disconnect,
    invoke,
    on,
    off,
  };
};
